import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Alert,
  Badge,
  Button,
  Input,
  Label,
  FormGroup,
  Table,
} from "reactstrap"

const TIPOS_GARANTIA = [
  { value: "hipotecaria", label: "Hipotecaria", icon: "bx-home", ltvMax: 70 },
  { value: "prendaria", label: "Prendaria", icon: "bx-car", ltvMax: 80 },
  { value: "fianza", label: "Fianza Personal", icon: "bx-user", ltvMax: 100 },
  {
    value: "sgr",
    label: "SGR (Sociedad de Garantía Recíproca)",
    icon: "bx-shield",
    ltvMax: 100,
  },
  {
    value: "fideicomiso",
    label: "Fideicomiso en Garantía",
    icon: "bx-lock",
    ltvMax: 85,
  },
  {
    value: "cesion_creditos",
    label: "Cesión de Créditos/Facturas",
    icon: "bx-receipt",
    ltvMax: 70,
  },
  {
    value: "sin_garantia",
    label: "Sin Garantía (Quirografario)",
    icon: "bx-file",
    ltvMax: 0,
  },
]

const GarantiaCard = ({ garantia, onEdit, onDelete }) => {
  const tipo = TIPOS_GARANTIA.find(t => t.value === garantia.tipo)
  const ltvColor =
    garantia.ltv <= 60 ? "success" : garantia.ltv <= 80 ? "warning" : "danger"

  return (
    <Card className="mb-3 border">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle bg-primary bg-opacity-10 p-2 me-3"
              style={{
                width: 45,
                height: 45,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className={`bx ${tipo?.icon || "bx-shield"} text-primary`}
                style={{ fontSize: 20 }}
              />
            </div>
            <div>
              <h6 className="mb-0">{tipo?.label || garantia.tipo}</h6>
              <small className="text-muted">{garantia.descripcion}</small>
            </div>
          </div>
          <div>
            <Button
              color="light"
              size="sm"
              className="me-1"
              onClick={() => onEdit(garantia)}
            >
              <i className="bx bx-edit" />
            </Button>
            <Button
              color="light"
              size="sm"
              onClick={() => onDelete(garantia.id)}
            >
              <i className="bx bx-trash text-danger" />
            </Button>
          </div>
        </div>

        <Row>
          <Col md={4}>
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Valor Avalúo</small>
              <strong className="text-primary">
                ${(garantia.valorAvaluo || 0).toLocaleString("es-AR")}
              </strong>
            </div>
          </Col>
          <Col md={4}>
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Monto Garantizado</small>
              <strong>
                ${(garantia.montoGarantizado || 0).toLocaleString("es-AR")}
              </strong>
            </div>
          </Col>
          <Col md={4}>
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">LTV</small>
              <strong className={`text-${ltvColor}`}>
                {garantia.ltv || 0}%
              </strong>
            </div>
          </Col>
        </Row>

        {garantia.gravamenes && garantia.gravamenes > 0 && (
          <Alert color="warning" className="mt-3 mb-0 py-2">
            <i className="bx bx-error me-2" />
            Tiene gravámenes previos por $
            {garantia.gravamenes.toLocaleString("es-AR")}
          </Alert>
        )}

        {garantia.ltv > (tipo?.ltvMax || 100) && (
          <Alert color="danger" className="mt-3 mb-0 py-2">
            <i className="bx bx-x-circle me-2" />
            LTV excede el máximo permitido ({tipo?.ltvMax}%) para este tipo de
            garantía
          </Alert>
        )}
      </CardBody>
    </Card>
  )
}

const FormGarantia = ({ garantia, montoCredito, onSave, onCancel }) => {
  const [form, setForm] = useState(
    garantia || {
      tipo: "",
      descripcion: "",
      valorAvaluo: "",
      gravamenes: "",
      fechaAvaluo: "",
      tasador: "",
    },
  )

  const valorNeto =
    (parseFloat(form.valorAvaluo) || 0) - (parseFloat(form.gravamenes) || 0)
  const ltv = valorNeto > 0 ? Math.round((montoCredito / valorNeto) * 100) : 0
  const tipoConfig = TIPOS_GARANTIA.find(t => t.value === form.tipo)

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = () => {
    const montoGarantizado = Math.min(valorNeto, montoCredito)
    onSave({
      ...form,
      id: form.id || Date.now(),
      valorAvaluo: parseFloat(form.valorAvaluo) || 0,
      gravamenes: parseFloat(form.gravamenes) || 0,
      montoGarantizado,
      ltv,
    })
  }

  return (
    <Card className="border-primary">
      <CardHeader className="bg-primary text-white">
        {garantia ? "Editar Garantía" : "Nueva Garantía"}
      </CardHeader>
      <CardBody>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Tipo de Garantía *</Label>
              <Input
                type="select"
                value={form.tipo}
                onChange={e => handleChange("tipo", e.target.value)}
              >
                <option value="">Seleccionar...</option>
                {TIPOS_GARANTIA.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Descripción</Label>
              <Input
                type="text"
                placeholder="Ej: Inmueble calle X, Vehículo patente Y"
                value={form.descripcion}
                onChange={e => handleChange("descripcion", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        {form.tipo &&
          form.tipo !== "sin_garantia" &&
          form.tipo !== "fianza" && (
            <>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Valor de Avalúo ($) *</Label>
                    <Input
                      type="number"
                      value={form.valorAvaluo}
                      onChange={e =>
                        handleChange("valorAvaluo", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Gravámenes Existentes ($)</Label>
                    <Input
                      type="number"
                      value={form.gravamenes}
                      onChange={e => handleChange("gravamenes", e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Fecha de Avalúo</Label>
                    <Input
                      type="date"
                      value={form.fechaAvaluo}
                      onChange={e =>
                        handleChange("fechaAvaluo", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Tasador / Perito</Label>
                    <Input
                      type="text"
                      placeholder="Nombre del tasador"
                      value={form.tasador}
                      onChange={e => handleChange("tasador", e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <div className="p-3 bg-light rounded">
                    <Row>
                      <Col>
                        <small className="text-muted">Valor Neto</small>
                        <div className="fw-bold">
                          ${valorNeto.toLocaleString("es-AR")}
                        </div>
                      </Col>
                      <Col>
                        <small className="text-muted">LTV Calculado</small>
                        <div
                          className={`fw-bold text-${ltv <= 70 ? "success" : ltv <= 85 ? "warning" : "danger"}`}
                        >
                          {ltv}%
                          {tipoConfig && ltv > tipoConfig.ltvMax && (
                            <small className="text-danger ms-1">
                              (máx: {tipoConfig.ltvMax}%)
                            </small>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </>
          )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button color="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={!form.tipo}>
            {garantia ? "Guardar Cambios" : "Agregar Garantía"}
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

// Componente principal
const Garantias = ({
  montoCredito = 0,
  garantias = [],
  setGarantias,
  setGarantiasCompletas,
}) => {
  const [mostrarForm, setMostrarForm] = useState(false)
  const [editando, setEditando] = useState(null)

  // Calcular cobertura total
  const coberturaTotal = garantias.reduce(
    (sum, g) => sum + (g.montoGarantizado || 0),
    0,
  )
  const porcentajeCobertura =
    montoCredito > 0 ? Math.round((coberturaTotal / montoCredito) * 100) : 0

  useEffect(() => {
    // Garantías completas si cobertura >= 100% o si es quirografario aprobado
    const tieneQuirografario = garantias.some(g => g.tipo === "sin_garantia")
    const coberturaOK = porcentajeCobertura >= 100
    if (setGarantiasCompletas) {
      setGarantiasCompletas(coberturaOK || tieneQuirografario)
    }
  }, [garantias, porcentajeCobertura])

  const handleSave = garantia => {
    if (editando) {
      setGarantias(prev => prev.map(g => (g.id === garantia.id ? garantia : g)))
    } else {
      setGarantias(prev => [...prev, garantia])
    }
    setMostrarForm(false)
    setEditando(null)
  }

  const handleEdit = garantia => {
    setEditando(garantia)
    setMostrarForm(true)
  }

  const handleDelete = id => {
    if (window.confirm("¿Eliminar esta garantía?")) {
      setGarantias(prev => prev.filter(g => g.id !== id))
    }
  }

  return (
    <div>
      <h5 className="mb-3">Garantías</h5>
      <p className="text-muted mb-4">
        Configure las garantías que respaldarán la operación crediticia.
      </p>

      {/* Resumen */}
      <Card className="mb-4 bg-light">
        <CardBody>
          <Row className="align-items-center">
            <Col md={4}>
              <small className="text-muted">Monto del Crédito</small>
              <h4 className="mb-0">${montoCredito.toLocaleString("es-AR")}</h4>
            </Col>
            <Col md={4}>
              <small className="text-muted">Cobertura Total</small>
              <h4 className="mb-0">
                ${coberturaTotal.toLocaleString("es-AR")}
              </h4>
            </Col>
            <Col md={4}>
              <small className="text-muted">% Cobertura</small>
              <h4
                className={`mb-0 text-${porcentajeCobertura >= 100 ? "success" : "warning"}`}
              >
                {porcentajeCobertura}%
              </h4>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Lista de garantías */}
      {garantias.map(g => (
        <GarantiaCard
          key={g.id}
          garantia={g}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ))}

      {/* Formulario o botón agregar */}
      {mostrarForm ? (
        <FormGarantia
          garantia={editando}
          montoCredito={montoCredito}
          onSave={handleSave}
          onCancel={() => {
            setMostrarForm(false)
            setEditando(null)
          }}
        />
      ) : (
        <Button color="primary" outline onClick={() => setMostrarForm(true)}>
          <i className="bx bx-plus me-1" />
          Agregar Garantía
        </Button>
      )}

      {/* Alertas */}
      {garantias.length > 0 && porcentajeCobertura < 100 && (
        <Alert color="warning" className="mt-4">
          <i className="bx bx-info-circle me-2" />
          La cobertura actual ({porcentajeCobertura}%) es menor al 100% del
          monto solicitado.
        </Alert>
      )}
    </div>
  )
}

export default Garantias
